import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sun, Moon, Globe, LogOut, FileText, LogIn, Link, Check, Store, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from '../components/auth/LoginModal';
import LinkAccountModal from '../components/auth/LinkAccountModal';
import { useI18n } from '../contexts/I18nContext';
import LanguageModal from '../components/LanguageModal';
import LogoutModal from '../components/LogoutModal';
import PatchNotesModal from '../components/PatchNotesModal';
import { useStoreConfig } from '../hooks/useStoreConfig';
import AccountManagementModal from '../components/AccountManagementModal';
import RequireAuthModal from '../components/auth/RequireAuthModal';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { user, checkUserSignInMethods, linkGoogleAccountWithVerification } = useAuth();
  const { t, language } = useI18n();
  const { storeConfig: storeType } = useStoreConfig();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showPatchNotes, setShowPatchNotes] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLinkAccountModal, setShowLinkAccountModal] = useState(false);
  const [showAccountManagement, setShowAccountManagement] = useState(false);
  const [showRequireAuth, setShowRequireAuth] = useState(false);
  const [hasLinkedAccount, setHasLinkedAccount] = useState(false);
  const [linkingAccount, setLinkingAccount] = useState(false);

  // Handle direct Google account linking
  const handleLinkGoogleAccount = async () => {
    console.log('🔗 handleLinkGoogleAccount called');
    setLinkingAccount(true);
    try {
      console.log('🔗 Calling linkGoogleAccountWithVerification...');
      await linkGoogleAccountWithVerification();
      console.log('🔗 Link successful, refreshing status...');
      
      // Force refresh the account linking status immediately
      if (user?.email) {
        console.log('🔗 Checking sign-in methods for:', user.email);
        
        // Check from user.providerData first (most reliable)
        const hasEmailPassword = user.providerData.some((p: any) => p.providerId === 'password');
        const hasGoogle = user.providerData.some((p: any) => p.providerId === 'google.com');
        
        console.log('🔗 From providerData - Has email/password:', hasEmailPassword);
        console.log('🔗 From providerData - Has Google:', hasGoogle);
        
        if (hasEmailPassword && hasGoogle) {
          console.log('🔗 Account is linked! Setting hasLinkedAccount to true');
          setHasLinkedAccount(true);
        } else {
          // Fallback to API check
          const methods = await checkUserSignInMethods(user.email);
          console.log('🔗 Sign-in methods:', methods);
          
          const hasEmailPasswordAPI = methods.includes('password');
          const hasGoogleAPI = methods.includes('google.com');
          const isLinked = hasEmailPasswordAPI && hasGoogleAPI;
          
          console.log('🔗 From API - Has email/password:', hasEmailPasswordAPI);
          console.log('🔗 From API - Has Google:', hasGoogleAPI);
          console.log('🔗 From API - Is linked:', isLinked);
          
          setHasLinkedAccount(isLinked);
        }
      }
    } catch (error) {
      console.error('❌ Error linking Google account:', error);
      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        alert(errorMessage);
    } finally {
      console.log('🔗 Setting linkingAccount to false');
      setLinkingAccount(false);
    }
  };

  // Check if user has multiple auth providers (email + Google)
  useEffect(() => {
    const checkAccountLinking = async () => {
      if (user?.email) {
        try {
          console.log('🔍 Checking account linking status...');
          console.log('🔍 User email:', user.email);
          console.log('🔍 User providerData:', user.providerData);
          
          // Check from user.providerData first (more reliable)
          const hasEmailPassword = user.providerData.some((p: any) => p.providerId === 'password');
          const hasGoogle = user.providerData.some((p: any) => p.providerId === 'google.com');
          
          console.log('🔍 From providerData - Has email/password:', hasEmailPassword);
          console.log('🔍 From providerData - Has Google:', hasGoogle);
          
          // Also check from sign-in methods API as backup
          const methods = await checkUserSignInMethods(user.email);
          const hasEmailPasswordAPI = methods.includes('password');
          const hasGoogleAPI = methods.includes('google.com');
          
          console.log('🔍 From API - Sign-in methods:', methods);
          console.log('🔍 From API - Has email/password:', hasEmailPasswordAPI);
          console.log('🔍 From API - Has Google:', hasGoogleAPI);
          
          // Use providerData as primary source, API as backup
          const isLinked = (hasEmailPassword && hasGoogle) || (hasEmailPasswordAPI && hasGoogleAPI);
          
          console.log('🔍 Final result - Is linked:', isLinked);
          
          setHasLinkedAccount(isLinked);
        } catch (error) {
          console.error('❌ Error checking sign-in methods:', error);
          // Fallback to providerData only
          const hasEmailPassword = user.providerData.some((p: any) => p.providerId === 'password');
          const hasGoogle = user.providerData.some((p: any) => p.providerId === 'google.com');
          const isLinked = hasEmailPassword && hasGoogle;
          console.log('🔍 Fallback - Is linked:', isLinked);
          setHasLinkedAccount(isLinked);
        }
      } else {
        console.log('🔍 No user, setting hasLinkedAccount to false');
        setHasLinkedAccount(false);
      }
    };

    checkAccountLinking();
  }, [user, checkUserSignInMethods]);


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const buttonVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
      },
    },
    hover: {
      scale: 1.02,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
      },
    },
    tap: {
      scale: 0.98,
    },
  };

  const handleAccountManagementClick = () => {
    if (user) {
      // Usuario autenticado, mostrar modal de gestión de cuenta
      setShowAccountManagement(true);
    } else {
      // Usuario no autenticado, mostrar modal de requerimiento de autenticación
      setShowRequireAuth(true);
    }
  };

  const handleDownloadTerms = () => {
    // Create a simple text file with terms and conditions
    const termsContent = `
TÉRMINOS Y CONDICIONES - ZETA AI

1. ACEPTACIÓN DE TÉRMINOS
Al utilizar Zeta AI, usted acepta estos términos y condiciones.

2. USO DEL SERVICIO
- El servicio está destinado para uso comercial y personal
- No se permite el uso indebido del contenido generado
- El usuario es responsable del contenido que sube y genera

3. PROPIEDAD INTELECTUAL
- Los contenidos generados por IA son propiedad del usuario
- Zeta AI no reclama derechos sobre el contenido generado

4. PRIVACIDAD
- Respetamos su privacidad y protegemos sus datos
- No compartimos información personal con terceros

5. LIMITACIONES
- El servicio puede tener limitaciones técnicas
- No garantizamos disponibilidad 100% del servicio

6. MODIFICACIONES
- Nos reservamos el derecho de modificar estos términos
- Los cambios serán notificados a través de la plataforma

Fecha de última actualización: ${new Date().toLocaleDateString()}
    `;

    const blob = new Blob([termsContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'terminos-y-condiciones-zeta-ai.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen px-4 pt-20 pb-24">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 mb-6"
          >
            <ArrowLeft size={20} />
            <span>{t('back')}</span>
          </button>
          
          <h1 className="text-3xl md:text-4xl font-contrail font-bold text-gray-900 dark:text-gray-100">
            {t('settings')}
          </h1>
        </motion.div>

        {/* Settings Options */}
        <motion.div
          variants={itemVariants}
          className="space-y-4"
        >
          {/* Theme Toggle */}
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={toggleTheme}
            className="card p-6 w-full text-left group hover:shadow-lg transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl">
                  {isDark ? <Moon className="w-6 h-6 text-yellow-500" /> : <Sun className="w-6 h-6 text-yellow-500" />}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('lightDarkMode')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {isDark ? t('themeOn') : t('themeOff')}
                  </p>
                </div>
              </div>
              <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                isDark ? 'bg-primary-500' : 'bg-gray-300'
              }`}>
                <motion.span
                  animate={{ x: isDark ? 20 : 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  className="inline-block h-4 w-4 transform rounded-full bg-white shadow-lg"
                />
              </div>
            </div>
          </motion.button>

          {/* Account Management */}
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={handleAccountManagementClick}
            className="card p-6 w-full text-left group hover:shadow-lg transition-all duration-300"
          >
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                <User className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('manageAccountTitle')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {t('manageAccountDescription')}
                </p>
              </div>
            </div>
          </motion.button>

          {/* Language Selection */}
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => setShowLanguageModal(true)}
            className="card p-6 w-full text-left group hover:shadow-lg transition-all duration-300"
          >
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <Globe className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('language')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {t('currentLanguage')}: {language === 'es' ? t('spanish') : language === 'pt' ? t('portuguese') : t('english')}
                </p>
              </div>
            </div>
          </motion.button>


          {/* Authentication Section */}
          {user ? (
            // With session: Show different options based on account linking status
            <div className="flex space-x-4">
              {hasLinkedAccount ? (
                // Account already linked - show success message with same style as button
                <div className="card p-6 flex-1 text-left">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                      <Check className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {t('accountLinkedWithGoogle')}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {t('canLoginWithAnyMethod')}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                // Account not linked - show link button
                <motion.button
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  onClick={handleLinkGoogleAccount}
                  disabled={linkingAccount}
                  className="card p-6 flex-1 text-left group hover:shadow-lg transition-all duration-300 disabled:opacity-50"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                      <Link className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {linkingAccount ? t('linkingAccount') : t('connectWithGoogle')}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {t('linkGoogleAccountDescription')}
                      </p>
                    </div>
                  </div>
                </motion.button>
              )}

              {/* Logout Button - RED */}
              <motion.button
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => setShowLogoutModal(true)}
                className="card p-6 flex-1 text-left group hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
                    <LogOut className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {t('logout')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {t('logoutDescription')}
                    </p>
                  </div>
                </div>
              </motion.button>
            </div>
          ) : (
            // Without session: Show only login button - GREEN
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => setShowLoginModal(true)}
              className="card p-6 w-full text-left group hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <LogIn className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('signIn')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t('signInDescription')}
                  </p>
                </div>
              </div>
            </motion.button>
          )}

          {/* Terms and Conditions */}
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={handleDownloadTerms}
            className="card p-6 w-full text-left group hover:shadow-lg transition-all duration-300 bg-gray-50 dark:bg-slate-800"
          >
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gray-100 dark:bg-slate-700 rounded-xl">
                <FileText className="w-6 h-6 text-gray-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('termsAndConditions')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {t('downloadTerms')}
                </p>
              </div>
            </div>
          </motion.button>
        </motion.div>

        {/* Version Info */}
        <motion.div
          variants={itemVariants}
          className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-700"
        >
          <button
            onClick={() => setShowPatchNotes(true)}
            className="text-sm text-gray-500 dark:text-gray-400 font-mono hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200"
          >
            {t('version')}
          </button>
        </motion.div>

        {/* Modals */}
        <AnimatePresence>
          {showLanguageModal && (
            <LanguageModal
              isOpen={showLanguageModal}
              onClose={() => setShowLanguageModal(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showLogoutModal && (
            <LogoutModal
              isOpen={showLogoutModal}
              onClose={() => setShowLogoutModal(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showLoginModal && (
            <LoginModal
              isOpen={showLoginModal}
              onClose={() => setShowLoginModal(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showLinkAccountModal && (
            <LinkAccountModal
              isOpen={showLinkAccountModal}
              onClose={() => setShowLinkAccountModal(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showPatchNotes && (
            <PatchNotesModal
              isOpen={showPatchNotes}
              onClose={() => setShowPatchNotes(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showAccountManagement && (
            <AccountManagementModal
              isOpen={showAccountManagement}
              onClose={() => setShowAccountManagement(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showRequireAuth && (
            <RequireAuthModal
              isOpen={showRequireAuth}
              onClose={() => setShowRequireAuth(false)}
              onLoginClick={() => {
                setShowRequireAuth(false);
                setShowLoginModal(true);
              }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Settings;
